import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { selectUILoading } from './store/selectors/ui.selectors';
import { Store } from '@ngrx/store';
import { RouterOutlet } from '@angular/router';
import { RoutingState } from '@core/services/routing-state';
import { AuthenticationService } from '@services/backend/authentication.service';
import { AuthStore } from './store/auth.store';
import { ConsortiumStore } from './store/consortium.store';
import { AccessibilityService } from '@services/analytics-ui/allycat/accessibility.service';
import { ApplicationInsightsLogsService } from '@services/log/application-insights/application-insights-logs.service';
import { LoadingSpinnerComponent } from '@shared/ui/misc/loading-spinner/loading-spinner.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, LoadingSpinnerComponent],
    providers: [AccessibilityService],
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
    private location = inject(Location);
    private routingState = inject(RoutingState);
    private accessibilityService = inject(AccessibilityService);
    private appInsightsSevice = inject(ApplicationInsightsLogsService);
    private authStore = inject(AuthStore);
    private consortiumStore = inject(ConsortiumStore);

    protected loading = signal<boolean>(false);
    private readonly store: Store = inject(Store);

    constructor() {
        this.store
            .select(selectUILoading)
            .pipe(takeUntilDestroyed())
            .subscribe((loading: boolean) => this.loading.set(loading));

        effect(() => {
            const authenticated = this.authStore.isAuthenticated();
            if (authenticated) {
                this.consortiumStore.loadMembership();
            } else if (authenticated === false) {
                this.consortiumStore.clearMembership();
            }
        });
    }

    ngOnInit() {
        this.routingState.setUrlHistoryInLocalStore();
        this.accessibilityService.start();
        this.loadData();
        this.appInsightsSevice.initialize();
    }

    private loadData(): void {
        const path = this.location.path();
        if (!!sessionStorage.getItem(AuthenticationService.authTokenStoreKey)) {
            this.authStore.loadIdentity();
        } else if (!path.includes('/sso/')) {
            this.authStore.logout();
        }
    }
}
